import { useMemo, useRef, useState } from 'react'
import type { Folder } from '@features/vault/types'
import { useOutsideClick } from '@hooks/useOutsideClick'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * フォルダ絞込ドロップダウン (T-D-Vault-Folder の chip 列を置き換え)。
 *
 * 設計判断:
 *   - フォルダが大量 (1000+) になっても UI が崩れないよう、横並び chip ではなく
 *     単一トリガ + ポップオーバー (検索 + 縦スクロールリスト) にする。
 *   - トリガは現在の選択フォルダ名 (未選択時は「すべてのフォルダ」) を表示。
 *   - ポップオーバー内: 検索 input → 「すべてのフォルダ」 → フォルダ一覧 (filter 済) →
 *     フッタに「新しいフォルダ」「フォルダを管理」。
 *   - 外側クリック / 項目選択 / Escape で閉じる。
 *   - 描画のみ。フォルダ CRUD / route 遷移は親 (VaultListPage) に委譲。
 */

export interface VaultFolderSelectProps {
  folders: readonly Folder[]
  /** 現在選択中フォルダ id。null は「すべてのフォルダ」。 */
  activeFolderId: string | null
  /** フォルダ選択時 (null = すべて)。 */
  onSelect: (folderId: string | null) => void
  /** 「新しいフォルダ」クリック。 */
  onCreate: () => void
  /** 「フォルダを管理」クリック。folders.length === 0 のときは非表示。 */
  onManage: () => void
}

export const VaultFolderSelect = ({
  folders,
  activeFolderId,
  onSelect,
  onCreate,
  onManage,
}: VaultFolderSelectProps): JSX.Element => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement | null>(null)

  useOutsideClick(rootRef, open, () => setOpen(false))

  const activeFolder = activeFolderId ? (folders.find(f => f.id === activeFolderId) ?? null) : null

  const triggerLabel = activeFolder ? activeFolder.name : t('vault.folder.all')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return folders

    return folders.filter(f => f.name.toLowerCase().includes(q))
  }, [folders, query])

  const close = (): void => {
    setOpen(false)
    setQuery('')
  }

  const handleSelect = (folderId: string | null): void => {
    onSelect(folderId)
    close()
  }

  return (
    <div className="vault-folder-select" ref={rootRef}>
      <button
        type="button"
        className={`vault-folder-select-trigger${activeFolder ? ' is-active' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="vault-folder-select-trigger"
      >
        <Icon name="folder" aria-hidden="true" />
        <span className="vault-folder-select-label">{triggerLabel}</span>
        <Icon name="chevron-down" aria-hidden="true" />
      </button>

      {open && (
        <div
          className="vault-folder-select-popover"
          role="dialog"
          aria-label={t('vault.folder.label')}
          onKeyDown={ev => {
            if (ev.key === 'Escape') close()
          }}
        >
          {folders.length > 8 && (
            <div className="vault-folder-select-search">
              <Icon name="search" aria-hidden="true" />
              <input
                type="text"
                value={query}
                onChange={ev => setQuery(ev.target.value)}
                placeholder={t('vault.folder.search_placeholder')}
                aria-label={t('vault.folder.search_placeholder')}
                data-testid="vault-folder-select-search"
                autoFocus
              />
            </div>
          )}

          <ul className="vault-folder-select-list" role="listbox">
            <li>
              <button
                type="button"
                role="option"
                aria-selected={activeFolderId === null}
                className={`vault-folder-select-option${activeFolderId === null ? ' is-active' : ''}`}
                onClick={() => handleSelect(null)}
              >
                <Icon name="folder" aria-hidden="true" />
                <span>{t('vault.folder.all')}</span>
                {activeFolderId === null && (
                  <Icon name="check" aria-hidden="true" className="vault-folder-select-check" />
                )}
              </button>
            </li>
            {filtered.map(folder => (
              <li key={folder.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={activeFolderId === folder.id}
                  className={`vault-folder-select-option${activeFolderId === folder.id ? ' is-active' : ''}`}
                  onClick={() => handleSelect(folder.id)}
                  title={folder.name}
                >
                  <Icon name="folder" aria-hidden="true" />
                  <span className="vault-folder-select-option-name">{folder.name}</span>
                  {activeFolderId === folder.id && (
                    <Icon name="check" aria-hidden="true" className="vault-folder-select-check" />
                  )}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="vault-folder-select-empty">{t('vault.folder.search_empty')}</li>
            )}
          </ul>

          <div className="vault-folder-select-footer">
            <button
              type="button"
              className="vault-folder-select-action"
              onClick={() => {
                close()
                onCreate()
              }}
              data-testid="vault-folder-new"
            >
              <Icon name="plus" aria-hidden="true" />
              {t('vault.folder.new_button')}
            </button>
            {folders.length > 0 && (
              <button
                type="button"
                className="vault-folder-select-action"
                onClick={() => {
                  close()
                  onManage()
                }}
                data-testid="vault-folder-manage"
              >
                <Icon name="settings" aria-hidden="true" />
                {t('vault.folder.manage_button')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
